import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  Renderer2,
  signal,
  input,
  output,
  ChangeDetectionStrategy,
  effect,
} from '@angular/core';
import { BehaviorSubject, combineLatest, of, Subscription, take, tap } from 'rxjs';
import { Stack } from 'src/app/common/dataStructures/stack';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from 'src/app/models/api/jsonhal.model';
import { PermissionEnum } from 'src/app/models/auth0/permission.enum';
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { IPaginationNavigationModel } from 'src/app/models/paginationNavigation.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITableColumn } from 'src/app/models/tableColumns';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { agentStepsActivityTableColumns } from 'src/app/models/tableColumns/aiAgentStepActivityTableColumn';
import { AiAgentStepActivityTableRowViewModel } from 'src/app/models/viewModels/aiAgentStepActivityTableRowViewModel';
import { ToggleOption } from 'src/app/components/option-toggle/option-toggle.component';

import {
  AgentClientApplication,
  AgentStepModel,
  BaseAgentStepModel,
  AiService,
  StepType,
  LlmProvider,
  InLineControlAiService,
} from 'src/app/models/api/agentStep.model';
import { AiAgentStepsHttpService } from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { formatFromDateWithSeconds } from 'src/app/common/datetime_utility';
import { AiActivityFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiActivityFilters.model';
import { getAiServiceLogoPath, getClientApplicationIconPath } from 'src/app/common/aiServiceUtils';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SidePanelService } from '../../../features/side-panel/services/side-panel.service';
import { AgentStepsActionsService } from 'src/app/services/actions/agent-steps/agent-steps-actions.service';
import { SearchActivityHttpService } from 'src/app/services/httpServices/detection/searchActivityService/search-activity-http.service';
import {
  InLineActivityStepTableRowViewModel,
  transformInLineActivityStepToTableRowViewModel,
} from 'src/app/models/viewModels/InLineActivityStepTableRowView.model';
import { InLineActivityStepModel } from 'src/app/models/api/inLineActivityStep.model';
import { AiInLineActivityFiltersModel } from 'src/app/models/filters/implementations/pageFilters/aiInLineActivityFilters.model';
import { UnifiedPipelineService } from 'src/app/services/infra/unifiedPipeline/unified-pipeline.service';
import { RSA_DEMO_AI_SERVICES } from 'src/app/models/selectOptions/rsaDemoAiServices';

@Component({
  selector: 'app-enterprise-ai-activity',
  templateUrl: './enterprise-ai-activity.component.html',
  styleUrls: ['./enterprise-ai-activity.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnterpriseAiActivityComponent implements OnInit {
  public selectedToggle = input<ToggleOption>('option1');
  public option1Text = input<string>('Option 1');
  public option2Text = input<string>('Option 2');
  public option1Icon = input<string>('radar');
  public option2Icon = input<string>('brain');
  public toggleChange = output<ToggleOption>();

  public columns = signal<ITableColumn[]>(agentStepsActivityTableColumns);
  public agentStepResourcesCurrentPage = signal<AiAgentStepActivityTableRowViewModel[]>([]);
  public inLineControlResourcesCurrentPage = signal<AiAgentStepActivityTableRowViewModel[]>([]);
  public currentTableData$: BehaviorSubject<AiAgentStepActivityTableRowViewModel[]> = new BehaviorSubject<
    AiAgentStepActivityTableRowViewModel[]
  >([]);
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public showNoData = false;
  public isFiltersMenuOpen = false;
  private _previousToggle: ToggleOption | null = null;

  public readonly defaultPageSize: number;
  public pageFilters: AiActivityFiltersModel;
  public aiInlineActivityPageFilters: AiInLineActivityFiltersModel;
  public optionsAreSelected = false;
  public pageInfo$: BehaviorSubject<ITablePageInfo>;
  public totalItems = 0;
  public totalThreads: number | undefined = undefined;
  public resourcesCount: BehaviorSubject<number> = new BehaviorSubject(-1);
  public featureFlagTypes = FeatureFlagTypes;

  public permissionsEnum = PermissionEnum;

  private _currentAgentStepResourcesById: {
    [agentStepResourceId: string]: BaseAgentStepModel;
  } = {};
  private _currentInLineControlResourcesById: {
    [inLineControlResourceId: string]: InLineActivityStepTableRowViewModel;
  } = {};
  private readonly _notAvailableString: string;

  private _paginationNavigation: IPaginationNavigationModel;

  private _firstPageSubscription$: Subscription;
  private _fetchByPageSubscription$: Subscription;
  private _rsaMockActivities: AgentStepModel[] | null = null;
  private _iamDemoMockActivities: AgentStepModel[] | null = null;
  private _isOnFirstPage = true;
  private readonly _rsaDemoServiceSet = new Set([
    ...RSA_DEMO_AI_SERVICES,
    'langchain',
  ]);

  // Watch for tab changes to reset filters and manage inline parameter
  private _tabChangeEffect = effect(() => {
    const toggle = this.selectedToggle();
    this._handleTabChange(toggle);
  });

  constructor(
    public permissionsService: PermissionsService,
    public featureFlagsService: FeatureFlagsService,
    private readonly _sidePanel: SidePanelService,
    private readonly _logger: LoggerService,
    private readonly _aiActivityHttpService: AiAgentStepsHttpService,
    private readonly _filtersService: FiltersService,
    private readonly _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService,
    private readonly _renderer: Renderer2,
    private readonly _el: ElementRef,
    private readonly _destroyRef: DestroyRef,
    private readonly _agentStepsActionsService: AgentStepsActionsService,
    private readonly _searchActivityHttpService: SearchActivityHttpService,
    private readonly _unifiedPipelineService: UnifiedPipelineService,
    private readonly _httpClient: HttpClient
  ) {
    this.defaultPageSize = this._aiActivityHttpService.defaultPageSize;
    this._notAvailableString = this._labelsProviderPipe.transform('common.notAvailable');
    this._initializePageInfo();
  }

  ngOnInit(): void {
    this.pageFilters = this._filtersService.getFilters(PageWithFiltersEnum.AiActivity) as AiActivityFiltersModel;
    this.aiInlineActivityPageFilters = this._filtersService.getFilters(
      PageWithFiltersEnum.InLineActivity
    ) as AiInLineActivityFiltersModel;

    // When UnifiedPipeline is enabled, initialize the filter model with current toggle mode
    // This sets up the correct Client Application and AI Service filter options
    if (this._unifiedPipelineService.isEnabled()) {
      this.pageFilters.setToggleMode(this.selectedToggle(), false);
    }

    this._subscribeToHandleFiltersOnChange();
    this._subscribeToSortOnChange();

    this._activeFiltersHeightService.height$
      .pipe(
        tap((height: number) =>
          this._renderer.setStyle(this._el.nativeElement, '--applied-filters-height', `${height}px`, 1)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
    // Don't load data immediately - let the filter onUpdate$ subscription handle it
    // This allows QueryParamsService to process URL params before the initial fetch
  }

  public tablesFiltersChange(selectedOptions: ISelectOption[]) {
    if (this._unifiedPipelineService.shouldUseUnifiedEndpoint(this.selectedToggle())) {
      // When UnifiedPipeline is enabled or AIDR toggle is selected, use the pageFilters
      this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
    } else {
      this.aiInlineActivityPageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
    }
  }

  public onPagination(operation: PaginationOperationEnum) {
    if (!this.isLoading$.value) {
      if (operation == PaginationOperationEnum.next) {
        this._paginationNavigation.previousPage.push(this._paginationNavigation.currentPage);
      }

      const pageUrl =
        operation == PaginationOperationEnum.next
          ? this._paginationNavigation.nextPage
          : this._paginationNavigation.previousPage.pop();

      this._isOnFirstPage =
        operation !== PaginationOperationEnum.next && this._paginationNavigation.previousPage.size() === 0;

      if (pageUrl) {
        if (this._unifiedPipelineService.shouldUseUnifiedEndpoint(this.selectedToggle())) {
          this._doPaginationWhenTokenExist(pageUrl);
        } else {
          this._fetchByPageInLineControl(pageUrl);
        }
      }
    }
  }

  public filtersOnMenuStateChange(menuState: boolean) {
    this.isFiltersMenuOpen = menuState;
  }

  public rowSelected(selection: AiAgentStepActivityTableRowViewModel): void {
    if (this.selectedToggle() === 'option1' || this._unifiedPipelineService.isEnabled()) {
      const url = new URL(window.location.href);
      if (url.searchParams.has('inline')) {
        url.searchParams.delete('inline');
        history.replaceState(null, '', url.toString());
      }
      this._sidePanel.openAgentStepDetails(selection);
    } else if (this.selectedToggle() === 'option2') {
      const url = new URL(window.location.href);
      url.searchParams.set('inline', 'true');
      history.replaceState(null, '', url.toString());

      this._sidePanel.openInLineStepDetails(selection as unknown as InLineActivityStepTableRowViewModel, {
        inline: true,
      });
    }
  }

  public rowUnselected = () => this._sidePanel.close();

  public onToggleChange(option: ToggleOption): void {
    this.toggleChange.emit(option);

    // When UnifiedPipeline is enabled, update the filter model's toggle mode
    // This switches Client Application and AI Service filter options
    if (this._unifiedPipelineService.isEnabled()) {
      this.pageFilters.setToggleMode(option);
    }

    this._switchDataSource(option);
  }

  private _loadDataBasedOnToggle(option?: ToggleOption): void {
    const toggleValue = option || this.selectedToggle();

    if (this._unifiedPipelineService.shouldUseUnifiedEndpoint(toggleValue)) {
      // Use unified endpoint with source filter applied
      const filtersWithSource = this._unifiedPipelineService.applySourceFilter(
        this.pageFilters.getSelectedFilters(),
        toggleValue
      );
      this._fetchFirstPage(filtersWithSource, this.pageFilters.sortedColumn$.value);
    } else {
      // Legacy behavior: use In-Line specific endpoint
      this._fetchFirstPageInLineControl(
        this.aiInlineActivityPageFilters.getSelectedFilters(),
        this.aiInlineActivityPageFilters.sortedColumn$.value
      );
    }
  }

  private _switchDataSource(option?: ToggleOption): void {
    this.agentStepResourcesCurrentPage.set([]);
    this.inLineControlResourcesCurrentPage.set([]);
    this.currentTableData$.next([]);
    this._loadDataBasedOnToggle(option);
  }

  private _subscribeToHandleFiltersOnChange() {
    // Subscribe to regular activity filters (used for Audit Log toggle, or both toggles when UnifiedPipeline is enabled)
    this.pageFilters.onUpdate$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((selectedFilters) => {
      this.resourcesCount.next(-1);
      this.optionsAreSelected = this.pageFilters.getAllSelectedOptions().length > 0;

      // When UnifiedPipeline is enabled, pageFilters is used for both toggles
      // Otherwise, only fetch when Audit Log toggle is selected (option1)
      if (this._unifiedPipelineService.isEnabled() || this.selectedToggle() === 'option1') {
        const filtersWithSource = this._unifiedPipelineService.applySourceFilter(
          selectedFilters,
          this.selectedToggle()
        );
        this._fetchFirstPage(filtersWithSource, this.pageFilters.sortedColumn$.value);
      }
    });

    // Subscribe to inline activity filters
    this.aiInlineActivityPageFilters.onUpdate$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((selectedFilters) => {
        this.resourcesCount.next(-1);
        this.optionsAreSelected = this.aiInlineActivityPageFilters.getAllSelectedOptions().length > 0;

        // Only fetch if In-Line toggle is selected (option2)
        if (this.selectedToggle() === 'option2') {
          if (this._unifiedPipelineService.isEnabled()) {
            // When UnifiedPipeline is enabled, use the unified endpoint with source filter
            const filtersWithSource = this._unifiedPipelineService.applySourceFilter(
              selectedFilters,
              this.selectedToggle()
            );
            this._fetchFirstPage(filtersWithSource, this.aiInlineActivityPageFilters.sortedColumn$.value);
          } else {
            // Legacy behavior: use In-Line specific endpoint
            this._fetchFirstPageInLineControl(selectedFilters, this.aiInlineActivityPageFilters.sortedColumn$.value);
          }
        }
      });
  }

  private _subscribeToSortOnChange() {
    // Subscribe to regular activity sort changes (used for Audit Log toggle, or both toggles when UnifiedPipeline is enabled)
    this.pageFilters.sortedColumn$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((newSort) => {
      // When UnifiedPipeline is enabled, pageFilters is used for both toggles
      // Otherwise, only fetch when Audit Log toggle is selected (option1)
      if (this._unifiedPipelineService.isEnabled() || this.selectedToggle() === 'option1') {
        const filtersWithSource = this._unifiedPipelineService.applySourceFilter(
          this.pageFilters.getSelectedFilters(),
          this.selectedToggle()
        );
        this._fetchFirstPage(filtersWithSource, newSort);
      }
    });

    // Subscribe to inline activity sort changes
    this.aiInlineActivityPageFilters.sortedColumn$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((newSort) => {
      // Only fetch if In-Line toggle is selected (option2)
      if (this.selectedToggle() === 'option2') {
        if (this._unifiedPipelineService.isEnabled()) {
          // When UnifiedPipeline is enabled, use the unified endpoint with source filter
          const filtersWithSource = this._unifiedPipelineService.applySourceFilter(
            this.aiInlineActivityPageFilters.getSelectedFilters(),
            this.selectedToggle()
          );
          this._fetchFirstPage(filtersWithSource, newSort);
        } else {
          // Legacy behavior: use In-Line specific endpoint
          this._fetchFirstPageInLineControl(this.aiInlineActivityPageFilters.getSelectedFilters(), newSort);
        }
      }
    });
  }

  private _fetchByPage(continuationToken: string) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    this._fetchByPageSubscription$ = this._aiActivityHttpService
      .getAgentStepsByContinuationToken(continuationToken)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(
        (response) => this._handleAgentStepCollectionResponse(response),
        (error) => this._fetchRequestOnError(error)
      );
  }

  private _fetchFirstPage(filterOptions: ISelectedFilters, sortData?: SortColumn) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();
    this._isOnFirstPage = true;

    const anyDemoEnabled =
      this._isRsaDemoEnabled() || this._isIamDemoEnabled();
    if (anyDemoEnabled && this._isOnlyRsaServicesSelected(filterOptions)) {
      this._firstPageSubscription$ = of(this._emptyResponse())
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (r) => this._handleAgentStepCollectionResponse(r),
        });
      return;
    }

    const stripped = anyDemoEnabled
      ? this._stripRsaServicesFromFilters(filterOptions)
      : filterOptions;

    this._firstPageSubscription$ = this._aiActivityHttpService
      .startGetAgentSteps(stripped, sortData)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => this._handleAgentStepCollectionResponse(response),
        error: (err) => this._fetchRequestOnError(err),
      });
  }

  private async _handleAgentStepCollectionResponse(response: JsonHalCollection<AgentStepModel>) {
    this._handlePaginationUrls(response._links);
    this._handlePageInfo(response.page);
    this.totalThreads = response.aggregations?.['totalThreads'];

    const items = response._embedded.items;
    this._currentAgentStepResourcesById = {};

    const anyDemoEnabled =
      this._isRsaDemoEnabled() || this._isIamDemoEnabled();

    if (!items || items.length == 0) {
      if (anyDemoEnabled) {
        this._loadRsaMockActivities([]);
      } else {
        this.showNoData = true;
        this.agentStepResourcesCurrentPage.set([]);
        this.currentTableData$.next([]);
        this.isLoading$.next(false);
      }
      return;
    }

    this.showNoData = false;
    const apiViewModels = items.map((item) => this._transformResourceToViewModel(item));

    if (anyDemoEnabled) {
      this._loadRsaMockActivities(apiViewModels);
    } else {
      this.agentStepResourcesCurrentPage.set(apiViewModels);
      this.currentTableData$.next(apiViewModels);
      this.isLoading$.next(false);
    }
    await this._agentStepsActionsService.setActionsForAgentSteps(items, apiViewModels);
  }

  private _cancelOngoingHttpRequest() {
    if (this._firstPageSubscription$) {
      this._firstPageSubscription$.unsubscribe();
    }

    if (this._fetchByPageSubscription$) {
      this._fetchByPageSubscription$.unsubscribe();
    }
  }

  private _fetchRequestOnError(error: HttpErrorResponse) {
    this.agentStepResourcesCurrentPage.set([]);
    this.isLoading$.next(false);
    this.showNoData = true;
    this._httpErrorHandlerService.handle(error);
  }

  private _tableBehaviorChangeBeforeFetch() {
    this.agentStepResourcesCurrentPage.set([]);
    this.totalThreads = undefined;
    this.isLoading$.next(true);
  }

  private _handlePaginationUrls(links: JsonHalLinks) {
    if (!this._paginationNavigation) {
      this._paginationNavigation = {
        currentPage: links.self.href,
        nextPage: links.next?.href,
        previousPage: new Stack<string>(),
      } as IPaginationNavigationModel;
    } else {
      this._paginationNavigation.currentPage = links.self.href;
      this._paginationNavigation.nextPage = links.next?.href;
    }
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo) {
    this.totalItems = pageInfo.totalItems;
    const normalized: JsonHalCollectionPageInfo = {
      number: pageInfo.number ?? 1,
      size: pageInfo.size,
      totalItems: pageInfo.totalItems,
      totalPages: pageInfo.totalPages,
    };
    const tablePageInfo = jsonHalPageInfoToTablePageInfo(normalized, this.defaultPageSize);

    if (!this.pageInfo$) {
      this.pageInfo$ = new BehaviorSubject(tablePageInfo);
    } else {
      this.pageInfo$.next(tablePageInfo);
    }
  }

  private _getSafeAiServiceLogoPath(service: string | AiService): string {
    try {
      return getAiServiceLogoPath(service as AiService);
    } catch {
      this._logger.warn(`Unsupported AI service: ${service}, using default icon`);
      return 'assets/images/logo/unknown-bot.svg';
    }
  }

  private _transformResourceToViewModel(step: AgentStepModel): AiAgentStepActivityTableRowViewModel {
    this._currentAgentStepResourcesById[step.id] = step;
    return {
      rowId: step.id,
      id: step.id,
      integrationId: step.integration?.id,
      actorName: step.mainActor?.displayName,
      timestamp: step.timestamp ? formatFromDateWithSeconds(step.timestamp) : this._notAvailableString,
      stepType: this._displayCamelCaseStringPipe.transform(step.type),
      threadId: step.thread?.id,
      menuActions: [],
      clickable: false,
      platformIcon: this._getSafeAiServiceLogoPath(step.service),
      application: this._displayCamelCaseStringPipe.transform(
        AgentClientApplication[step.clientContext?.clientApplication]
      ),
      agentName: this._displayCamelCaseStringPipe.transform(step.botDetails?.displayName),
      step: step,
      clientApplicationIcon: getClientApplicationIconPath(step.clientContext?.clientApplication),
      clientApplication: Object.values(AgentClientApplication).includes(
        step.clientContext?.clientApplication as AgentClientApplication
      )
        ? this._displayCamelCaseStringPipe.transform(AgentClientApplication[step.clientContext?.clientApplication])
        : this._displayCamelCaseStringPipe.transform(step.clientContext?.clientApplication),
    } as AiAgentStepActivityTableRowViewModel;
  }

  private _doPaginationWhenTokenExist(url: string) {
    const continuationToken = this._tokenExtractor(url);

    if (continuationToken) {
      this._fetchByPage(continuationToken);
    }
  }

  private _tokenExtractor(url: string): string | null {
    const continuationToken = new URLSearchParams(url.slice(url.indexOf('?'))).get('continuationToken');
    if (!continuationToken) {
      this._logger.error('Failed to extract continuation token from json hal next activity resource page link');
      return null;
    }

    return continuationToken;
  }

  private _initializePageInfo() {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: 0,
      maxSize: this._aiActivityHttpService.defaultPageSize,
      totalItems: 0,
      totalPages: 1,
    });
  }

  private _fetchFirstPageInLineControl(selectedFilters: ISelectedFilters, sortData?: SortColumn) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    this._firstPageSubscription$ = this._searchActivityHttpService
      .getInLineControlActivitySteps(undefined, sortData, selectedFilters)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response: JsonHalCollection<InLineActivityStepModel>) =>
          this._handleActivitiesInLineControlCollectionResponse({
            ...response,
            _embedded: {
              items: response._embedded.items.map((m) => transformInLineActivityStepToTableRowViewModel(m)),
            },
          } as unknown as JsonHalCollection<InLineActivityStepTableRowViewModel>),
        error: (err: HttpErrorResponse) => this._fetchRequestOnError(err),
      });
  }

  private async _handleActivitiesInLineControlCollectionResponse(
    response: JsonHalCollection<InLineActivityStepTableRowViewModel>
  ) {
    this._handlePaginationUrls(response._links);
    this._handlePageInfo(response.page);
    this.totalThreads = response.aggregations?.['totalThreads'];

    const items = response._embedded.items;
    this._currentAgentStepResourcesById = {};

    if (!items?.length) {
      this.showNoData = true;
      this.inLineControlResourcesCurrentPage.set([]);
      this.currentTableData$.next([]);
    } else {
      this.showNoData = false;
      const agentStepViewModels = items.map((item) => this._transformInLineControlToViewModel(item));
      this.inLineControlResourcesCurrentPage.set(agentStepViewModels);
      this.currentTableData$.next(agentStepViewModels);
    }
    this.isLoading$.next(false);
  }

  private _transformInLineControlToViewModel(
    step: InLineActivityStepTableRowViewModel
  ): AiAgentStepActivityTableRowViewModel {
    this._currentInLineControlResourcesById[step.id] = step;
    const actorName = step.userName
      ? step.userName === 'Unknown'
        ? this._notAvailableString
        : step.userName
      : this._notAvailableString;
    const agentName = this._getInLineControlAgentName(step);
    return {
      rowId: step.id,
      id: step.id,
      integrationId: step.integrationId || this._notAvailableString,
      actorName: actorName,
      timestamp: step.timestamp ? formatFromDateWithSeconds(new Date(step.timestamp)) : this._notAvailableString,
      platformIcon: this._getSafeAiServiceLogoPath(this._getAiService(step.applicationName)),
      stepType: this._displayCamelCaseStringPipe.transform(this._getInLineControlActivityStepType(step.stepType)),
      threadId: step.threadId || this._notAvailableString,
      agentName: agentName,
      application: step.applicationName || this._notAvailableString,
      step: this._mapInlineToAgentStepModel(step),
      clientApplicationIcon: this._getInLineControlClientApplicationIcon(step.applicationName),
      clientApplication: this._getInLineControlClientApplicationName(step.applicationName),
      menuActions: [],
      clickable: false,
    } as AiAgentStepActivityTableRowViewModel;
  }

  // Minimal inline → AgentStepModel mapping for list row parity
  private _mapInlineToAgentStepModel(step: InLineActivityStepTableRowViewModel): AgentStepModel {
    const type: StepType = (() => {
      const stepTypeLower = (step.stepType || '').toLowerCase();
      // Normalize step type to handle different formats (AIMessageStep, aiMessageStep, etc.)
      if (stepTypeLower.includes('toolcallrequeststep')) return StepType.toolInvocationRequestStep;
      if (stepTypeLower.includes('toolcallresultstep')) return StepType.toolInvocationResultStep;
      if (stepTypeLower.includes('toolcallstep')) return StepType.toolInvocationStep;
      if (stepTypeLower.includes('agenthandoffrequest')) return StepType.agentHandoffRequestStep;
      if (stepTypeLower.includes('agenthandoffresponse')) return StepType.agentHandoffResponseStep;
      if (stepTypeLower.includes('user')) return StepType.userMessageStep;
      if (stepTypeLower.includes('aimessage')) return StepType.aiMessageStep;
      if (stepTypeLower.includes('rag')) return StepType.ragStep;
      if (stepTypeLower.includes('trigger')) return StepType.triggerStep;
      return StepType.aiMessageStep;
    })();

    const service: AiService | InLineControlAiService = (() => {
      const app = step.applicationName ?? '';
      // Check specific cases first before generic patterns
      if (app.toLowerCase().includes('copilot studio')) {
        return AiService.copilotStudio;
      }
      if (app.includes('GitHub')) {
        return AiService.gitHub;
      }
      if (app.includes('Claude')) {
        return AiService.claude;
      }
      if (app.toLowerCase().includes('perplexity')) {
        return AiService.perplexity;
      }
      if (app.toLowerCase().includes('agentkit')) {
        return AiService.agentKit;
      }
      if (app.toLowerCase().includes('cursor')) {
        return AiService.cursor;
      }
      // Generic Copilot check comes after specific cases
      if (app.includes('Copilot') || app.includes('M365') || app === 'Copilot' || app === 'Copilot App') {
        return AiService.m365Copilot;
      }
      return this._getAiService(step.applicationName);
    })();

    const agentDisplay = this._getInLineControlAgentName(step);

    type InlineBase = Partial<BaseAgentStepModel> &
      Partial<{
        isTranscriptFetchingAllowed: boolean;
        attachedDocuments: unknown[];
        toolInvocations: { toolDisplayName?: string; toolOrigin?: string }[];
        triggerIconUrl: string;
        executionId?: string;
      }> & { _inlineMode?: boolean; _inlineTranscript?: string };

    const base: InlineBase = {
      id: step.id,
      timestamp: new Date(step.timestamp),
      type,
      service: service as AiService,
      integration: { id: step.integrationId },
      zenityAccountId: step.zenityAccountId,
      thread: { id: step.threadId ?? '' },
      mainActor: { displayName: step.userName, id: step.userName },
      relatedActors: [],
      botDetails: {
        displayName: agentDisplay,
        llmProvider: LlmProvider.custom,
        modelName: 'N/A',
        platformInfo: { resourceId: '' },
      },
      clientContext: { clientApplication: step.applicationName as unknown as AgentClientApplication },
      indicators: [],
      stepTriggerIds: [],
      _inlineMode: true,
    };

    switch (type) {
      case StepType.aiMessageStep:
      case StepType.userMessageStep:
      case StepType.agentHandoffRequestStep:
      case StepType.agentHandoffResponseStep: {
        base.isTranscriptFetchingAllowed = undefined;
        base.attachedDocuments = [];
        if (step.transcript) base._inlineTranscript = step.transcript;
        break;
      }
      case StepType.toolInvocationStep: {
        base.toolInvocations = [{ toolDisplayName: step.toolDisplayName || 'Tool', toolOrigin: undefined }];
        base.triggerIconUrl = '';
        break;
      }
      case StepType.toolInvocationResultStep: {
        base.toolInvocations = [{ toolDisplayName: step.toolDisplayName || 'Tool', toolOrigin: undefined }];
        // Try to extract executionId from result JSON if available
        if (step.result) {
          try {
            const resultObj = JSON.parse(step.result);
            if (resultObj?.executionId) {
              base.executionId = resultObj.executionId;
            }
          } catch (err) {
            console.warn('Error parsing result as JSON', err);
          }
        }
        break;
      }
      default:
        break;
    }

    return base as AgentStepModel;
  }

  private _getInLineControlAgentName(step: InLineActivityStepTableRowViewModel): string {
    if (step.agentName) {
      return step.agentName;
    } else if (step.applicationName) {
      switch (step.applicationName) {
        case 'GitHub Copilot (Visual Studio)':
        case 'GitHub Copilot (Web)':
          return 'GitHub Copilot';
        case 'Copilot App':
        case 'Copilot':
          return 'M365 Copilot';
        case 'Claude Desktop':
          return 'Claude';
        default:
          return step.applicationName;
      }
    }
    return this._notAvailableString;
  }

  private _getInLineControlActivityStepType(stepType: string): string {
    switch (stepType) {
      case 'AIMessageStep':
        return 'AI Message Step';
      default:
        return stepType;
    }
  }

  private _getAiService(applicationName: string): AiService | InLineControlAiService {
    switch ((applicationName ?? '').toLowerCase()) {
      case 'custom agent':
      case 'customagent':
      case 'custom_agent':
        return AiService.customAgent;
      case 'claude desktop':
        return AiService.claude;
      case 'slack':
        return AiService.slack;
      case 'github copilot (visual studio)':
      case 'github copilot (web)':
        return AiService.gitHub;
      case 'azure foundry':
        return AiService.azureAiFoundry;
      case 'copilot studio':
        return AiService.copilotStudio;
      case 'chatgpt':
        return AiService.chatGptEnterprise;
      case 'copilot app':
      case 'm365copilot':
      case 'cps':
      case 'copilot':
        return AiService.m365Copilot;
      case 'bedrockagentcore':
        return AiService.bedrockAgentCore;
      case 'perplexity':
        return AiService.perplexity;
      case 'cursor':
        return AiService.cursor;
      default:
        return InLineControlAiService.unknown;
    }
  }

  private _getInLineControlClientApplicationIcon(applicationName: string): string {
    switch ((applicationName ?? '').toLowerCase()) {
      case 'github copilot (visual studio)':
        return 'assets/images/logo/vscode.svg';
      case 'github copilot (web)':
        return 'assets/images/common/globe.svg';
      case 'copilot studio':
        return 'assets/images/logo/copilotStudio.svg';
      case 'copilot app':
      case 'm365copilot':
      case 'cps':
      case 'copilot':
        return 'assets/images/logo/m365Copilot.svg';
      case 'claude desktop':
        return 'assets/images/logo/claude-logo.svg';
      case 'chatgpt':
        return 'assets/images/logo/chatGptEnterprise.svg';
      case 'slack':
        return 'assets/images/logo/slack-logo.svg';
      case 'azure foundry':
        return 'assets/images/logo/azure-ai-foundry.svg';
      case 'bedrockagentcore':
        return 'assets/images/logo/amazon-bedrock-agentcore-logo.svg';
      case 'cursor':
        return 'assets/images/logo/cursor-logo.svg';
      default:
        return 'assets/images/logo/agent-client-app-other.svg';
    }
  }

  private _getInLineControlClientApplicationName(applicationName: string): string {
    switch (applicationName) {
      case 'GitHub Copilot (Visual Studio)':
        return 'Visual Studio Code';
      case 'GitHub Copilot (Web)':
        return 'Web';
      case 'Copilot App':
      case 'm365copilot':
      case 'cps':
      case 'Copilot':
        return 'M365 Copilot';
      default:
        return applicationName;
    }
  }

  private _fetchByPageInLineControl(url: string) {
    const continuationToken = new URLSearchParams(url.slice(url.indexOf('?'))).get('continuationToken');
    if (!continuationToken) {
      this._fetchFirstPageInLineControl(
        this.aiInlineActivityPageFilters.getSelectedFilters(),
        this.aiInlineActivityPageFilters.sortedColumn$.value
      );
      return;
    }

    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    this._fetchByPageSubscription$ = this._searchActivityHttpService
      .getInLineControlActivitySteps(continuationToken)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(
        (response: JsonHalCollection<InLineActivityStepModel>) =>
          this._handleActivitiesInLineControlCollectionResponse({
            ...response,
            _embedded: {
              items: response._embedded.items.map((m) => transformInLineActivityStepToTableRowViewModel(m)),
            },
          } as unknown as JsonHalCollection<InLineActivityStepTableRowViewModel>),
        (error) => this._fetchRequestOnError(error)
      );
  }

  private _handleTabChange(toggle: ToggleOption): void {
    // Only handle tab changes after filters are initialized and when toggle actually changes
    if (
      this.pageFilters &&
      this.aiInlineActivityPageFilters &&
      this._previousToggle !== null &&
      this._previousToggle !== toggle
    ) {
      // Note: URL 'q' parameter is cleared by parent component
      // We don't reset the actual filter state, only the URL changes
    }
    this._previousToggle = toggle;
  }

  private _isRsaDemoEnabled(): boolean {
    return this.featureFlagsService.isFeatureEnabled(
      FeatureFlagTypes.RsaDemoRuntimeBoundaries
    );
  }

  private _isIamDemoEnabled(): boolean {
    return this.featureFlagsService.isFeatureEnabled(
      FeatureFlagTypes.SpmIamDemoAmex
    );
  }

  private _applyDynamicTimestamps(
    steps: AgentStepModel[]
  ): AgentStepModel[] {
    const now = new Date();
    const base = new Date(now);
    base.setMinutes(now.getMinutes() - 5);

    const lastIdx = steps.length - 1;
    return steps.map((s, i) => {
      const offset = (lastIdx - i) * 15 * 1000;
      const ts = new Date(base.getTime() - offset);
      return {
        ...s,
        timestamp: ts.toISOString() as unknown as Date,
      };
    });
  }

  private _emptyResponse(): JsonHalCollection<AgentStepModel> {
    return {
      _embedded: { items: [] },
      _links: { self: { href: '' } },
      page: {
        number: 1,
        size: this.defaultPageSize,
        totalItems: 0,
        totalPages: 1,
      },
    } as unknown as JsonHalCollection<AgentStepModel>;
  }

  private _stripRsaServicesFromFilters(filters: ISelectedFilters): ISelectedFilters {
    if (!filters.aiService?.length) return filters;
    return {
      ...filters,
      aiService: filters.aiService.filter((s) => !this._rsaDemoServiceSet.has(s)),
    };
  }

  private _isOnlyRsaServicesSelected(filters: ISelectedFilters): boolean {
    if (!filters.aiService?.length) return false;
    return filters.aiService.every((s) => this._rsaDemoServiceSet.has(s));
  }

  private _loadRsaMockActivities(
    apiViewModels: AiAgentStepActivityTableRowViewModel[]
  ): void {
    const rsaEnabled = this._isRsaDemoEnabled();
    const iamDemoEnabled = this._isIamDemoEnabled();
    const anyDemoEnabled = rsaEnabled || iamDemoEnabled;

    if (!this._isOnFirstPage || !anyDemoEnabled) {
      this.showNoData = apiViewModels.length === 0;
      this.agentStepResourcesCurrentPage.set(apiViewModels);
      this.currentTableData$.next(apiViewModels);
      this.isLoading$.next(false);
      return;
    }

    const rsaReady = !rsaEnabled || !!this._rsaMockActivities;
    const iamDemoReady =
      !iamDemoEnabled || !!this._iamDemoMockActivities;

    if (rsaReady && iamDemoReady) {
      this._prependMockActivities(apiViewModels);
      return;
    }

    const loads: import('rxjs').Observable<AgentStepModel[]>[] = [];

    if (rsaEnabled && !this._rsaMockActivities) {
      loads.push(
        this._httpClient.get<AgentStepModel[]>(
          'assets/mockData/rsaActivityMockData.json'
        )
      );
    }
    if (iamDemoEnabled && !this._iamDemoMockActivities) {
      loads.push(
        this._httpClient.get<AgentStepModel[]>(
          'assets/mockData/iamDemoActivityMockData.json'
        )
      );
    }

    if (loads.length === 0) {
      this._prependMockActivities(apiViewModels);
      return;
    }

    combineLatest(loads)
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (results) => {
          let idx = 0;
          if (rsaEnabled && !this._rsaMockActivities) {
            this._rsaMockActivities = results[idx++];
          }
          if (iamDemoEnabled && !this._iamDemoMockActivities) {
            this._iamDemoMockActivities =
              this._applyDynamicTimestamps(results[idx]);
          }
          this._prependMockActivities(apiViewModels);
        },
        error: () => {
          this.showNoData = apiViewModels.length === 0;
          this.agentStepResourcesCurrentPage.set(apiViewModels);
          this.currentTableData$.next(apiViewModels);
          this.isLoading$.next(false);
        },
      });
  }

  private _prependMockActivities(
    apiViewModels: AiAgentStepActivityTableRowViewModel[]
  ): void {
    const selectedServices =
      this.pageFilters?.getSelectedFilters()?.aiService ?? [];
    const hasServiceFilter = selectedServices.length > 0;
    const serviceSet = new Set(selectedServices);

    let allMockActivities: AgentStepModel[] = [];

    if (this._iamDemoMockActivities) {
      let iamFiltered = this._iamDemoMockActivities;
      if (hasServiceFilter) {
        iamFiltered = iamFiltered.filter((step) =>
          serviceSet.has(step.service)
        );
      }
      allMockActivities = [...allMockActivities, ...iamFiltered];
    }

    if (this._rsaMockActivities) {
      let rsaFiltered = this._rsaMockActivities;
      if (hasServiceFilter) {
        rsaFiltered = rsaFiltered.filter((step) =>
          serviceSet.has(step.service)
        );
      }
      allMockActivities = [...allMockActivities, ...rsaFiltered];
    }

    const mockViewModels = allMockActivities.map((step) =>
      this._transformResourceToViewModel(step)
    );
    const combined = [...mockViewModels, ...apiViewModels].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() -
        new Date(a.timestamp).getTime()
    );
    this.showNoData = combined.length === 0;
    this.agentStepResourcesCurrentPage.set(combined);
    this.currentTableData$.next(combined);
    this.isLoading$.next(false);
  }
}
